import {
  Box,
  TextField,
  Typography,
  Stack,
  Chip,
  Divider,
} from "@mui/material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { useAppTranslation } from "../../hooks/useAppTranslation";

type VatRate = 23 | 8 | 5 | 0 | "zw";

type VatLine = {
  vat: VatRate;
  amount: number;
  description: string;
};

type Props = {
  priceType: "netto" | "brutto";
  currencyCode?: string;
  lines: VatLine[];
  onChange: (lines: VatLine[]) => void;
};

function calculateGross(amount: number, vat: VatRate) {
  if (vat === "zw" || vat === 0) return amount;
  return amount * (1 + vat / 100);
}

export default function AddInvoiceVatLines({
  priceType,
  currencyCode,
  lines,
  onChange,
}: Props) {
  const { t } = useAppTranslation("common");
  const currencyLabel = currencyCode || "PLN";

  return (
    <Box sx={{ mt: 1 }}>
      <Divider sx={{ my: 2.5 }} />

      <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
        <ReceiptIcon sx={{ fontSize: 20, color: "text.secondary" }} />
        <Typography variant="subtitle2" color="text.secondary">
          {t("Pozycje faktury wg stawek VAT")}
        </Typography>
      </Stack>

      {lines.map((line, index) => {
        const gross =
          priceType === "netto"
            ? calculateGross(line.amount, line.vat)
            : line.amount;

        return (
          <Box
            key={line.vat}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 2,
              mb: 1.5,
              bgcolor: "#F8FAFC",
              transition: "border-color 0.2s",
              "&:hover": { borderColor: "primary.main" },
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 2fr auto",
                gap: 2,
                alignItems: "center",
              }}
            >
              <TextField
                label={t(`VAT ${line.vat}% (${priceType})`)}
                type="number"
                onFocus={(e) => {
                  e.target.select();
                }}
                size="small"
                value={line.amount}
                onChange={(e) => {
                  const updated = [...lines];
                  updated[index] = {
                    ...line,
                    amount: Number(e.target.value),
                  };
                  onChange(updated);
                }}
              />

              <TextField
                label={t("Opis (opcjonalny)")}
                size="small"
                value={line.description}
                onChange={(e) => {
                  const updated = [...lines];
                  updated[index] = {
                    ...line,
                    description: e.target.value,
                  };
                  onChange(updated);
                }}
              />

              <Chip
                label={`${t("Brutto")}: ${gross.toFixed(2)} ${currencyLabel}`}
                size="small"
                color={line.amount > 0 ? "primary" : "default"}
                variant="outlined"
                sx={{ fontWeight: 600, minWidth: 140, justifyContent: "center" }}
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}